import {
  Abilities as BaseAbilities,
  Permission as BasePermission,
  Permissions as BasePermissions,
  RequiredPermission as BaseRequiredPermission,
  Rule as BaseRule,
  WithRequiredPermissionsProps as BaseWithRequiredPermissionsProps,
  MongoAbility,
  PermissionGenerator,
  permissionsFactory,
  RawRuleOf,
  Role,
} from '@eigtech/ui-shared-auth'
import { Forbidden } from '@eigtech/ui-shared-auth-web'
import { z } from 'zod'

export const ActionSchema = z.union([
  z.literal('create'),
  z.literal('read'),
  z.literal('update'),
  z.literal('delete'),

  z.literal('receiveWorkDetails'),
])
export type Action = z.infer<typeof ActionSchema>

export const SubjectSchema = z.union([
  z.literal('admin'),
  z.literal('assignments'),
  z.literal('bill'),
  z.literal('claim'),
  z.literal('communication'),
  z.literal('contact'),
  z.literal('document'),
  z.literal('draftDocument'),
  z.literal('estimate'),
  z.literal('job'),
  z.literal('meeting'),
  z.literal('message'),
  z.literal('note'),

  z.literal('approver'),
  z.literal('coordinator'),
  z.literal('deskAdjuster'),
  z.literal('dispatcher'),
  z.literal('fieldAdjuster'),
  z.literal('fieldTechnician'),
  z.literal('primaryContact'),
  z.literal('projectManager'),
  z.literal('subcontractor'),
])
export type Subject = z.infer<typeof SubjectSchema>

export type Abilities = BaseAbilities<Action, Subject>

export type Permission = BasePermission<Action, Subject>
export type Permissions = BasePermissions<Action, Subject>

export type Rule = BaseRule<Action, Subject>

export type RequiredPermission = BaseRequiredPermission<Action, Subject>

export type WithRequiredPermissionsProps = BaseWithRequiredPermissionsProps<Action, Subject>

const assignmentAccess: PermissionGenerator<Action, Subject> = (__permissions, roles) => {
  const newPermissions: RawRuleOf<MongoAbility<[Action, Subject]>>[] = [
    { action: 'read', subject: 'coordinator' },
    { action: 'read', subject: 'deskAdjuster' },
    { action: 'read', subject: 'fieldAdjuster' },
    { action: 'read', subject: 'projectManager' },
  ]

  const employeeCohort: Role[] = ['TPLAP - Field Technician']
  const isEmployeeCohort = roles.some((role) => employeeCohort.includes(role))

  if (isEmployeeCohort) {
    return [
      ...newPermissions,
      { action: 'read', subject: 'approver' },
      { action: 'read', subject: 'dispatcher' },
      { action: 'read', subject: 'fieldTechnician' },
      { action: 'read', subject: 'primaryContact' },
      { action: 'read', subject: 'subcontractor' },
    ]
  }

  const contractorCohort: Role[] = ['TPLAP - Subcontractor']
  const isContractorCohort = roles.some((role) => contractorCohort.includes(role))

  if (isContractorCohort) {
    return [
      ...newPermissions,
      { action: 'read', subject: 'approver' },
      { action: 'read', subject: 'dispatcher' },
      { action: 'read', subject: 'fieldTechnician' },
      { action: 'read', subject: 'primaryContact' },
    ]
  }

  return newPermissions
}

const {
  AbilityContext,
  Can,
  ability,
  updateAbility,
  useAbility,
  useAbilityContext,
  useHasRequiredPermissions,
  useKeepAbilityUpToDate,
  withRequiredPermissions,
} = permissionsFactory({
  additionalPermissionsGenerators: [assignmentAccess],
  ForbiddenComponent: Forbidden,
})

export {
  ability,
  AbilityContext,
  Can,
  updateAbility,
  useAbility,
  useAbilityContext,
  useHasRequiredPermissions,
  useKeepAbilityUpToDate,
  withRequiredPermissions,
}
